import React from "react";
import { BoxCard } from "./BoxCard";
import { ItemCard } from "./SubProductCard";
import { Box } from "./BoxSchedule";
import { OrderTotals } from "./OrderTotals";
import { BoxItem } from "./types";
import { Button } from "@/components/Button";
import { boxTotals, droppsProductToProductCardProduct } from "./helpers";

const BoxItemProductCard = ({
  boxItem,
  disabled,
  onSelect,
  onDelay,
  onAddToNextBox,
  onSkip,
}: {
  boxItem: BoxItem;
  disabled: boolean;
  onSelect: (boxItem: BoxItem) => void;
  onDelay?: (boxItem: BoxItem) => void;
  onSkip?: (boxItem: BoxItem) => void;
  onAddToNextBox?: (boxItem: BoxItem) => void;
}) => {
  const subscriptionActions = [
    {
      displayText: "edit",
      action: () => onSelect(boxItem),
    },
    ...(onDelay
      ? [
          {
            displayText: "delay",
            action: () => onDelay(boxItem),
          },
        ]
      : []),
    ...(onSkip
      ? [
          {
            displayText: "skip",
            action: () => onSkip(boxItem),
          },
        ]
      : []),
    ...(onAddToNextBox
      ? [
          {
            displayText: "add_to_next_box",
            action: () => onAddToNextBox(boxItem),
          },
        ]
      : []),
  ];

  const { product, variant, rechargeItem } = boxItem;
  const productCardProduct = droppsProductToProductCardProduct({
    product,
    variant,
  });

  return (
    <ItemCard
      key={rechargeItem.id}
      responsive={true}
      product={productCardProduct}
      price={rechargeItem.price}
      shippingInfo={rechargeItem.cadence ?? undefined}
      subscriptionActions={subscriptionActions}
      quantity={rechargeItem.quantity}
      disabled={disabled}
      data-subscription-id={rechargeItem.id}
      scentBg={"white"}
    />
  );
};

type BoxListProps = {
  boxes: Box[];
  requestInProgress: boolean;
  onSelect: (item: BoxItem) => void;
  onDelay?: (item: BoxItem) => void;
  onSkip?: (item: BoxItem) => void;
  onAddToNextBox?: (item: BoxItem) => void;
  onAddItem: (item: BoxItem) => void;
  onDelayBox?: (shipmentId: string) => void;
  onSkipBox?: (shipmentId: string) => void;
  onShipWithNextBox?: (shipmentId: string) => void;
  onOrderNow: (shipmentId: string) => void;
};

const BoxList = ({
  boxes,
  requestInProgress,
  onSelect,
  onDelay,
  onAddToNextBox,
  onAddItem,
  onOrderNow,
  onDelayBox,
  onShipWithNextBox,
  onSkipBox,
  onSkip,
}: BoxListProps) => {
  const displayOfBoxes = [];
  for (let i = 0; i < boxes.length; i++) {
    const { address, nextChargeDate, items: boxItems, id, discount } = boxes[i];
    const totals = boxTotals(boxItems, discount);
    const discountInfo =
      totals.discounts !== undefined && discount
        ? { amount: totals.discounts, name: discount.name }
        : undefined;
    const isAllOneTime = !boxItems.some(
      (boxItem) => boxItem.rechargeItem.status === "ACTIVE"
    );

    const boxItemEntry = boxItems.map((s) => {
      const isOneTime = s.rechargeItem.status === "ONETIME";
      return !isOneTime ? (
        <li key={s.rechargeItem.id}>
          <BoxItemProductCard
            boxItem={s}
            onSelect={onSelect}
            onSkip={onSkip ?? undefined}
            onAddToNextBox={onAddToNextBox ?? undefined}
            disabled={requestInProgress}
          />
        </li>
      ) : (
        <li key={s.rechargeItem.id}>
          <BoxItemProductCard
            boxItem={s}
            onSelect={onSelect}
            onDelay={onDelay ?? undefined}
            onAddToNextBox={onAddToNextBox ?? undefined}
            disabled={requestInProgress}
          />
        </li>
      );
    });

    displayOfBoxes.push(
      <li key={id}>
        <BoxCard
          key={id}
          shipDate={nextChargeDate}
          address={address}
          data-address-id={address.id}
          data-schedule-id={id}
          actions={
            <>
              <div className="flex md:block">
                <Button
                  className="md:btn-sm lg:btn-base w-full md:w-auto md:text-left lg:p-0"
                  variant="text"
                  data-portal-button="add-product"
                  onClick={() => onAddItem(boxItems[0])}
                >
                  + Add Another Product
                </Button>
              </div>
              {
                <div className="flex flex-col space-y-4 md:col-span-2 md:flex-row md:justify-end md:space-x-4 md:space-y-0">
                  {onDelayBox && isAllOneTime && (
                    <Button
                      variant="outline"
                      data-portal-button="delay-this-box"
                      onClick={() => onDelayBox(id)}
                    >
                      Delay This Box
                    </Button>
                  )}
                  {onSkipBox && !isAllOneTime && (
                    <Button
                      variant="outline"
                      data-portal-button="skip-this-box"
                      onClick={() => onSkipBox(id)}
                    >
                      Skip This Box
                    </Button>
                  )}
                  {onShipWithNextBox && (
                    <Button
                      variant="outline"
                      data-portal-button="ship-with-next-box"
                      onClick={() => onShipWithNextBox(id)}
                    >
                      Ship With Next Box
                    </Button>
                  )}
                  <Button
                    data-portal-button="order-now"
                    onClick={() => onOrderNow(id)}
                  >
                    Order Now
                  </Button>
                </div>
              }
            </>
          }
          endChildren={
            <OrderTotals
              className="md:ml-auto md:w-64"
              shippingCost={totals.shipping}
              subtotal={totals.lineItemsPrice}
              discount={discountInfo}
              estimatedTotal={totals.estimatedTotal}
            />
          }
          onAddProduct={() => onAddItem(boxItems[0])}
        >
          <ul className="space-y-4">{boxItemEntry}</ul>
        </BoxCard>
      </li>
    );
  }

  return (
    <>
      {displayOfBoxes.length > 0 ? (
        <ol className="space-y-8">{displayOfBoxes}</ol>
      ) : (
        <div className="flex flex-col bg-white p-5 shadow">
          <p className="body text-md">
            Get started by adding a new item or subscription!
          </p>
        </div>
      )}
    </>
  );
};

export { BoxList };
